import { Inject, Injectable, Injector } from '@angular/core'
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http'
import { Observable, throwError, timer } from 'rxjs'
import { catchError, finalize, retry, take } from 'rxjs/operators'
import { AuthService } from '../../core/auth/auth.service'
import { ErrorService } from '../../core/services/error.service'
import { Store } from '@ngrx/store'
import { MainViewActions } from '../../core/layout/state/action-types'
import { selectMessages } from '../../core/layout/state/main-view.selectors'
import { ImportantMessage, ImportantMessageSeverity } from '../../core/layout/state/important-message.model'

// DEV-731: přechodné stavy, u kterých má smysl request zopakovat — backend se restartuje
// (rolling restart při deploji) nebo geoserver nestihl odpovědět.
const RETRYABLE_STATUSES = [502, 503, 504]

// Retryujeme jen idempotentní metody. Podle RFC 9110 §9.2.2 jsou idempotentní i PUT a DELETE,
// ale náš backend je psaný jako běžné CRUD, ne s idempotencí na papíře — DELETE na druhý pokus
// klidně vrátí 404 a PUT může mít vedlejší efekty. Jsme tedy záměrně striktnější než spec;
// není to přehlédnutí RFC. U POST by retry mohl věc založit dvakrát, kdyby backend request
// zpracoval a spadl až při odesílání odpovědi.
const IDEMPOTENT_METHODS = ['GET', 'HEAD']

const MAX_RETRIES = 3
const RETRY_BASE_DELAY_MS = 5000

// Od kolikátého pokusu dát uživateli vědět. První retry proběhne za 5 s a většina výpadků při
// rolling restartu se v něm schová — bliknout hláškou, která hned zmizí, je horší než ticho.
// Od druhého čeká uživatel dalších 10 s, tam už ta informace cenu má.
const ANNOUNCE_FROM_RETRY = 2

// Vlastní id v seznamu důležitých zpráv. Reducer `updateImportantMessages` zachovává zprávy
// s id <= -1, takže backendový stateUpdate ji nesmaže. Obsazené jsou -1 (blockRequests),
// -2 a -3 (ČÚZK) a -4 (skipped) — všechny v main-view.effects.ts. Kdo přidá další, ať sem doplní.
const RETRY_MESSAGE_ID = -6
const RETRY_MESSAGE = 'Server neodpovídá, zkoušíme požadavek zopakovat…'

// `notice` = oranžový pruh s ikonou refresh, mírnější než červený `warning`. Retry není chyba,
// je to průběžný stav — nemá vypadat stejně jako „aplikace je zablokovaná".
const RETRY_MESSAGE_SEVERITY: ImportantMessageSeverity = 'notice'

@Injectable()
export class ErrorHandlingInterceptorService implements HttpInterceptor {

    // Kolik requestů právě ohlásilo, že retryuje. Zprávu přidáváme při přechodu 0 → 1
    // a odebíráme při 1 → 0, aby ji souběžné requesty nepřidávaly a nemazaly navzájem.
    private announcedRetries = 0

    // All dependencies are resolved lazily via Injector to prevent NG0200 circular DI:
    // HTTP_INTERCEPTORS chain is built lazily on the first HTTP request. If any constructor
    // dependency transitively needs HttpClient, the chain rebuild recurses → cycle.
    // By keeping the constructor empty (only Injector), the interceptor cannot trigger any
    // construction during chain build. Deps are fetched only inside intercept() callbacks,
    // by which time the chain is fully built.
    constructor(@Inject(Injector) private injector: Injector) {
    }

    private get authService(): AuthService { return this.injector.get(AuthService) }
    private get store(): Store { return this.injector.get(Store) }
    private get errorService(): ErrorService { return this.injector.get(ErrorService) }

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        // Ohlásil tenhle konkrétní request, že retryuje? Drží se per-request, protože
        // `announcedRetries` je společné čítadlo pro všechny běžící requesty.
        let announced = false

        return next.handle(req).pipe(
            // DEV-731: retry stojí PŘED catchError větví záměrně. Eskalace na
            // `blockRequests: 'ALL'` (a s ní banner a následný window.location.reload()
            // v setBlockRequests$) se tím dispatchne až po vyčerpání pokusů — tedy méně
            // často než dřív, nikdy víc. Samotná eskalační cesta se nemění.
            retry({
                count: MAX_RETRIES,
                delay: (error: HttpErrorResponse, retryCount: number) => {
                    if (!this.isRetryable(req, error)) {
                        return throwError(() => error)
                    }

                    if (retryCount >= ANNOUNCE_FROM_RETRY && !announced) {
                        announced = true
                        this.announceRetrying()
                    }

                    // exponenciální backoff: 5 s / 10 s / 20 s
                    return timer(RETRY_BASE_DELAY_MS * Math.pow(2, retryCount - 1))
                }
            }),
            catchError((response: HttpErrorResponse) => {
                if (response.status === 503) {
                    this.store.dispatch(MainViewActions.setBlockRequests({
                        blockRequests: 'ALL'
                    }))
                }

                if (response.status === 401) {
                    // Skip handling for auth endpoints - let AuthGuard handle these
                    // to avoid race condition with competing router.navigate() calls
                    // Auth endpoints use pattern: {authUrl}/rest/auth/... (e.g., /rest/auth/access-token)
                    const isAuthEndpoint = req.url.includes('/rest/auth/')
                    if (!isAuthEndpoint) {
                        // DEV-3143: ať je důvod 401 jakýkoli, uživatel dostane okno „Platnost přihlášení vypršela“
                        // a přechod na login nesmí blokovat kontrola neuložených změn
                        this.authService.resetAuthContext()
                        this.authService.authorizationTokenExpired()
                    }
                }
                if (response.status === 500) {
                    this.errorService.resolveServerError(response)
                }
                return throwError(() => response)
            }),
            // Doběhne při úspěchu, chybě i odhlášení odběratele, takže zpráva nikdy nezůstane viset.
            finalize(() => {
                if (announced) {
                    announced = false
                    this.withdrawRetrying()
                }
            }),
        )
    }

    private isRetryable(req: HttpRequest<any>, error: HttpErrorResponse): boolean {
        return RETRYABLE_STATUSES.includes(error?.status) && IDEMPOTENT_METHODS.includes(req.method)
    }

    private announceRetrying(): void {
        this.announcedRetries += 1

        if (this.announcedRetries > 1) {
            return
        }

        this.store.select(selectMessages).pipe(take(1)).subscribe((messages: Array<ImportantMessage>) => {
            if (messages.some(message => message.id === RETRY_MESSAGE_ID)) {
                return
            }

            this.store.dispatch(MainViewActions.setImportantMessages({
                messages: [...messages, {
                    id: RETRY_MESSAGE_ID,
                    content: RETRY_MESSAGE,
                    severity: RETRY_MESSAGE_SEVERITY
                }]
            }))
        })
    }

    private withdrawRetrying(): void {
        this.announcedRetries -= 1

        if (this.announcedRetries > 0) {
            return
        }

        this.store.select(selectMessages).pipe(take(1)).subscribe((messages: Array<ImportantMessage>) => {
            if (!messages.some(message => message.id === RETRY_MESSAGE_ID)) {
                return
            }

            this.store.dispatch(MainViewActions.setImportantMessages({
                messages: messages.filter(message => message.id !== RETRY_MESSAGE_ID)
            }))
        })
    }
}
